/**
 * Endereço sempre "limpo": a barra do navegador mostra só o domínio (https://pmis.addigital.com.br), sem a tela em que a
 * pessoa está. A rota fica no estado de cada entrada do histórico do navegador, então voltar, avançar e recarregar a
 * página continuam funcionando. Um link com caminho (ex.: /projetos/123, vindo de um e-mail) abre a tela certa e, em
 * seguida, a barra volta a mostrar só o domínio. Os links da tela (href) continuam com o caminho completo: "abrir em nova
 * aba" e "copiar endereço do link" funcionam.
 */
import { useLayoutEffect, useState, type ReactNode } from 'react';
import { NavigationType, Router, createPath, parsePath, type Location, type Navigator, type To } from 'react-router-dom';

const KEY = 'pmisRoute';

/** O fim do endereço é a resposta da Microsoft ao login (#code=… ou #error=…), e não uma tela do PMIS? */
export const isAuthResponse = (hash: string) => /^#(.*&)?(code|error)=/.test(hash) && /[#&]state=/.test(hash);
interface Entry { path: string; state: unknown; key: string; idx: number }
type Listener = (s: { action: NavigationType; location: Location }) => void;

const toPath = (to: To) => (typeof to === 'string' ? to : createPath(to));
const toLocation = (path: string, state: unknown, key: string): Location => {
  const p = parsePath(path || '/');
  return { pathname: p.pathname || '/', search: p.search ?? '', hash: p.hash ?? '', state: state ?? null, key };
};
const newKey = () => Math.random().toString(36).slice(2, 10);

class HiddenHistory implements Navigator {
  action: NavigationType = NavigationType.Pop;
  location: Location;
  private idx = 0;
  private listeners = new Set<Listener>();

  constructor() {
    const saved = (window.history.state as Record<string, Entry> | null)?.[KEY];
    if (saved) { this.location = toLocation(saved.path, saved.state, saved.key); this.idx = saved.idx; }
    else this.location = toLocation(`${window.location.pathname}${window.location.search}${isAuthResponse(window.location.hash) ? '' : window.location.hash}`, null, 'default');
    this.write('replace');
    window.addEventListener('popstate', (e) => {
      const st = (e.state as Record<string, Entry> | null)?.[KEY];
      if (!st) return;
      this.action = NavigationType.Pop;
      this.location = toLocation(st.path, st.state, st.key);
      this.idx = st.idx;
      this.emit();
    });
  }

  private write(kind: 'push' | 'replace') {
    const entry: Entry = { path: createPath(this.location), state: this.location.state, key: this.location.key, idx: this.idx };
    if (kind === 'push') window.history.pushState({ [KEY]: entry }, '', '/');
    else window.history.replaceState({ [KEY]: entry }, '', '/');
  }

  private emit() { for (const l of this.listeners) l({ action: this.action, location: this.location }); }

  // Funções presas à instância: o React Router chama push/replace soltos (sem o objeto).
  listen = (l: Listener) => { this.listeners.add(l); return () => { this.listeners.delete(l); }; };
  createHref = (to: To) => toPath(to);
  encodeLocation = (to: To) => { const p = parsePath(toPath(to)); return { pathname: p.pathname ?? '/', search: p.search ?? '', hash: p.hash ?? '' }; };
  push = (to: To, state?: unknown) => {
    this.action = NavigationType.Push;
    this.location = toLocation(toPath(to), state, newKey());
    this.idx++;
    this.write('push');
    this.emit();
  };
  replace = (to: To, state?: unknown) => {
    this.action = NavigationType.Replace;
    this.location = toLocation(toPath(to), state, newKey());
    this.write('replace');
    this.emit();
  };
  go = (delta: number) => { window.history.go(delta); };
}

let history: HiddenHistory | null = null;
const getHistory = () => (history ??= new HiddenHistory());

export function HiddenUrlRouter({ children }: { children: ReactNode }) {
  const h = getHistory();
  const [s, setS] = useState({ action: h.action, location: h.location });
  useLayoutEffect(() => h.listen(setS), [h]);
  return <Router location={s.location} navigationType={s.action} navigator={h}>{children}</Router>;
}

/** Tela atual dentro do PMIS (caminho + busca), já que a barra de endereço mostra só o domínio. */
export const appPath = () => { const l = getHistory().location; return `${l.pathname}${l.search}`; };
/** Vai para uma tela sem criar entrada no histórico (ex.: voltar à tela de antes do login). */
export const appReplace = (path: string) => getHistory().replace(path);
/** Link completo de uma tela, para copiar e mandar (a barra de endereço não mostra). */
export const appLink = (path: string) => `${window.location.origin}${path}`;
